import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../api/cliente';
import { Paginacao, recortar } from '../componentes/Paginacao';
import {
  CabecalhoDaPagina,
  Carregando,
  Cartao,
  Ficha,
  TituloDeSecao,
  Vazio,
} from '../componentes/base';
import { data, moeda, plural } from '../util/formato';

/** Quantas linhas por bloco. Cabe numa tela sem rolagem longa. */
const POR_PAGINA = 20;

interface Candidato {
  readonly id: string;
  readonly nome: string;
  readonly clinica: { id: string; nome: string };
  readonly fase: string;
  readonly valorOriginal: number;
  readonly motivo: string;
  readonly aptoJudicial: boolean;
  readonly diasNaCarteira: number;
}

interface Prazo {
  readonly id: string;
  readonly numeroProcesso: string | null;
  readonly vara: string | null;
  readonly comarca: string | null;
  readonly proximoPrazo: string | null;
  readonly diasAteOPrazo: number;
  readonly vencido: boolean;
  readonly devedor: { id: string; nome: string; clinica: { nome: string } };
}

export function PaginaTriagem() {
  const { data: candidatos, isLoading } = useQuery({
    queryKey: ['candidatos-judicial'],
    queryFn: () => api.buscar<Candidato[]>('/painel/candidatos-judicial'),
  });

  const { data: prazos } = useQuery({
    queryKey: ['prazos'],
    queryFn: () => api.buscar<Prazo[]>('/processos/prazos', { dias: 30 }),
  });

  if (isLoading) return <Carregando />;

  const aptos = (candidatos ?? []).filter((c) => c.aptoJudicial);
  const pendentes = (candidatos ?? []).filter((c) => !c.aptoJudicial);

  return (
    <>
      <CabecalhoDaPagina
        titulo="Triagem judicial"
        descricao="Casos que já deveriam estar no judicial e prazos em curso"
      />

      {/* Os prazos vêm PRIMEIRO: perder prazo é o risco profissional mais concreto
          do escritório, e supera em urgência qualquer decisão de ajuizar. Esta
          lista NÃO pagina de propósito — são poucos, e esconder um prazo atrás de
          um botão "próxima" é exatamente como se perde um. */}
      {prazos && prazos.length > 0 && (
        <Cartao semPadding className="mb-6">
          <div className="p-5 pb-3">
            <TituloDeSecao auxiliar="próximos 30 dias">Prazos judiciais</TituloDeSecao>
          </div>
          <ul className="border-t">
            {prazos.map((p) => (
              <li
                key={p.id}
                className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3 last:border-0"
              >
                <div className="min-w-0">
                  <Link
                    to={`/devedores/${p.devedor.id}`}
                    className="font-medium text-[color:var(--texto)] hover:text-[color:var(--acento)]"
                  >
                    {p.devedor.nome}
                  </Link>
                  <p className="mt-0.5 text-xs text-[color:var(--texto-tenue)]">
                    {p.numeroProcesso && (
                      <span className="font-mono">{p.numeroProcesso}</span>
                    )}
                    {p.vara && ` · ${p.vara}`}
                    {p.comarca && `, ${p.comarca}`}
                  </p>
                </div>
                <div className="text-right">
                  <Ficha tom={p.vencido ? 'vencido' : p.diasAteOPrazo <= 7 ? 'atencao' : 'neutro'}>
                    {p.vencido
                      ? `vencido há ${plural(Math.abs(p.diasAteOPrazo), 'dia', 'dias')}`
                      : p.diasAteOPrazo === 0
                        ? 'vence hoje'
                        : `em ${plural(p.diasAteOPrazo, 'dia', 'dias')}`}
                  </Ficha>
                  <p className="tabular mt-0.5 text-xs text-[color:var(--texto-tenue)]">
                    {data(p.proximoPrazo)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </Cartao>
      )}

      {aptos.length > 0 && (
        <Cartao semPadding className="mb-6">
          <div className="p-5 pb-3">
            <TituloDeSecao auxiliar={`${aptos.length}`}>
              Prontos para ajuizar
            </TituloDeSecao>
            <p className="text-sm text-[color:var(--texto-suave)]">
              Documentação conferida e aptidão confirmada — só falta distribuir a ação.
            </p>
          </div>
          <BlocoDeCandidatos candidatos={aptos} />
        </Cartao>
      )}

      <Cartao semPadding>
        <div className="p-5 pb-3">
          <TituloDeSecao auxiliar={`${pendentes.length}`}>
            Aguardando documentação
          </TituloDeSecao>
          <p className="text-sm text-[color:var(--texto-suave)]">
            O caso já justifica o ajuizamento, mas o servidor recusa a confirmação enquanto
            faltar documento — ajuizar incompleto leva à extinção da ação.
          </p>
        </div>
        {pendentes.length === 0 ? (
          <Vazio titulo="Nenhum caso pendente de documentação." />
        ) : (
          <BlocoDeCandidatos candidatos={pendentes} />
        )}
      </Cartao>
    </>
  );
}

/**
 * Tabela de candidatos com paginação própria.
 *
 * O estado da página fica AQUI, e não na tela, porque os dois blocos rolam
 * independentes: avançar em "aguardando documentação" não deve mexer na lista de
 * quem já está pronto para ajuizar.
 */
function BlocoDeCandidatos({ candidatos }: { candidatos: readonly Candidato[] }) {
  const [pagina, setPagina] = useState(1);
  const recorte = recortar(candidatos, pagina, POR_PAGINA);

  return (
    <>
      <div className="rolagem-lateral border-t">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-[color:var(--superficie-afundada)] text-left text-[0.6875rem] uppercase tracking-[0.08em] text-[color:var(--texto-tenue)]">
              <th className="px-5 py-2.5 font-medium">Devedor</th>
              <th className="px-3 py-2.5 font-medium">Motivo</th>
              <th className="px-3 py-2.5 text-right font-medium">Dívida</th>
              <th className="px-5 py-2.5 text-right font-medium">Na carteira há</th>
            </tr>
          </thead>
          <tbody>
            {recorte.itens.map((c) => (
              <tr key={c.id} className="border-b last:border-0">
                <td className="px-5 py-3">
                  <Link
                    to={`/devedores/${c.id}`}
                    className="font-medium text-[color:var(--texto)] hover:text-[color:var(--acento)]"
                  >
                    {c.nome}
                  </Link>
                  <p className="text-[0.6875rem] text-[color:var(--texto-tenue)]">
                    {c.clinica.nome}
                  </p>
                </td>
                <td className="px-3 py-3 text-[color:var(--texto-suave)]">{c.motivo}</td>
                <td className="tabular px-3 py-3 text-right whitespace-nowrap">
                  {moeda(c.valorOriginal)}
                </td>
                <td className="tabular px-5 py-3 text-right whitespace-nowrap text-[color:var(--texto-suave)]">
                  {plural(c.diasNaCarteira, 'dia', 'dias')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Paginacao dados={recorte} aoMudarPagina={setPagina} nomeDoItem={['caso', 'casos']} />
    </>
  );
}
